import React, { useState, useEffect, useMemo } from 'react';
import { advanceApi, employeeApi } from '@/lib/api';
import { clampPageToTotal } from '@/lib/pagination';
import { useAuth } from '@/lib/auth';
import { canEditAdvances, canApproveAdvances } from '@/lib/roles';
import { formatCurrency } from '@/lib/payrollCalc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Wallet, Ban, History, TrendingDown, Search, Download, CheckCircle, XCircle, Printer, Pencil } from 'lucide-react';
import { downloadBlobFile } from '@/lib/exportFile';
import { AdvanceSlipModal } from '@/components/AdvanceSlipModal';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const emptyAdvanceForm = () => ({
  employeeId: '',
  totalAmount: '',
  monthlyDeduction: '',
  startMonth: String(new Date().getMonth() + 1),
  startYear: String(new Date().getFullYear()),
  reason: '',
});

const formatRequestedDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const asAdvanceList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.advances)) return value.advances;
  if (Array.isArray(value?.history)) return value.history;
  if (Array.isArray(value?.records)) return value.records;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const toAmount = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const unwrapAdvanceDetails = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return {};
  if (
    payload.data &&
    typeof payload.data === 'object' &&
    !Array.isArray(payload.data) &&
    payload.joiningDate == null &&
    payload.monthlySalary == null &&
    payload.monthlySalaryBreakdown == null
  ) {
    return payload.data;
  }
  return payload;
};

const prettifyDetailLabel = (key) => {
  const labels = {
    basic: 'Basic',
    hra: 'HRA',
    specialAllowance: 'Special Allowance',
    gross: 'Gross',
    inHand: 'In Hand',
    inHandSalary: 'In Hand',
    netPay: 'In Hand',
    totalDeductions: 'Deductions',
    epf: 'EPF',
    esic: 'ESIC',
    pf: 'PF',
    tds: 'TDS',
    ctc: 'CTC',
    monthly: 'Monthly',
    annual: 'Annual',
  };
  if (labels[key]) return labels[key];
  return String(key || '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
};

const HIDDEN_SALARY_KEYS = new Set(['basic', 'ctc', 'basicsalary', 'monthlybasic']);
const HIDDEN_SALARY_LABELS = new Set(['basic', 'ctc', 'basic salary']);

const isHiddenSalaryField = (key, label) => {
  const keyNorm = String(key || '').replace(/[\s_-]/g, '').toLowerCase();
  const labelNorm = String(label || '').trim().toLowerCase();
  return HIDDEN_SALARY_KEYS.has(keyNorm) || HIDDEN_SALARY_LABELS.has(labelNorm);
};

const salaryBreakdownEntries = (salary) => {
  if (salary == null || salary === '') return [];
  if (typeof salary === 'number' || typeof salary === 'string') {
    return [['Monthly Salary', salary]];
  }
  if (Array.isArray(salary)) {
    return salary
      .map((row) => {
        if (row == null) return null;
        if (typeof row === 'object') {
          const label = row.label || row.name || row.component || row.key;
          const amount = row.amount ?? row.monthly ?? row.value;
          if (!label || amount == null || typeof amount === 'object') return null;
          if (isHiddenSalaryField(row.key, label)) return null;
          return [String(label), amount];
        }
        return null;
      })
      .filter(Boolean);
  }
  if (!salary || typeof salary !== 'object') return [];
  const preferred = [
    'hra',
    'specialAllowance',
    'gross',
    'epf',
    'esic',
    'tds',
    'totalDeductions',
    'inHand',
    'inHandSalary',
    'netPay',
  ];
  const keys = [
    ...preferred.filter((key) => salary[key] != null),
    ...Object.keys(salary).filter((key) => !preferred.includes(key)),
  ];
  return keys
    .map((key) => {
      const value = salary[key];
      if (value == null || typeof value === 'object') return null;
      const label = prettifyDetailLabel(key);
      if (isHiddenSalaryField(key, label)) return null;
      return [label, value];
    })
    .filter(Boolean);
};

const normalizeDeductionMonths = (value) => {
  if (Array.isArray(value)) {
    return value.map((item, index) => {
      if (item == null || typeof item !== 'object') {
        return { label: `Month ${index + 1}`, amount: toAmount(item) };
      }
      const monthIndex = Number(item.month);
      const monthName =
        item.monthName ||
        item.label ||
        item.period ||
        (Number.isFinite(monthIndex) && monthIndex >= 1 && monthIndex <= 12
          ? `${MONTHS[monthIndex - 1]} ${item.year || ''}`.trim()
          : item.month || `Month ${index + 1}`);
      return {
        label: String(monthName),
        amount: toAmount(item.amount ?? item.deduction ?? item.actualMonthlyDeduction ?? item.value),
      };
    });
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).map(([label, amount]) => ({
      label,
      amount: toAmount(
        amount && typeof amount === 'object'
          ? amount.amount ?? amount.deduction ?? amount.value
          : amount
      ),
    }));
  }
  return [];
};

const normalizeAdvanceDetails = (payload) => {
  const source = unwrapAdvanceDetails(payload);
  const salary =
    source.monthlySalaryBreakdown ??
    source.monthlySalary ??
    source.salaryBreakdown ??
    source.salary ??
    {};
  const oldAdvance = source.oldAdvanceSummary ?? source.oldAdvance ?? source.oldAdvances ?? {};
  const lastMonths =
    source.actualMonthlyDeductionLastMonths ??
    source.actualMonthlyDeductionsLastMonths ??
    source.lastSixMonthsDeductions ??
    source.lastMonthsDeductions ??
    [];

  return {
    joiningDate: source.joiningDate ?? source.dateOfJoining ?? '',
    monthlySalaryEntries: salaryBreakdownEntries(salary),
    setMonthlyDeductionAmount: toAmount(
      source.setMonthlyDeductionAmount ?? source.monthlyDeductionAmount ?? source.monthlyDeduction
    ),
    balanceAdvance: toAmount(
      source.balanceAdvance ?? source.advanceBalance ?? source.remainingAdvance ?? source.remainingAmount
    ),
    lastMonths: normalizeDeductionMonths(lastMonths),
    oldAdvanceSource: oldAdvance && typeof oldAdvance === 'object' ? oldAdvance : {},
  };
};

const AdvanceReviewSalaryDetails = ({ loading, error, details }) => {
  if (loading && !details) {
    return (
      <div className="flex items-center justify-center py-5 text-sm text-muted-foreground">
        <div className="animate-spin w-5 h-5 border-2 border-[#0047AB] border-t-transparent rounded-full mr-2" />
        Loading salary details...
      </div>
    );
  }

  if (error && !details) {
    return <p className="text-sm text-red-600">{error}</p>;
  }

  if (!details) return null;

  return (
    <div className="space-y-3" data-testid="advance-review-salary-details">
      <p className="text-xs uppercase tracking-widest text-muted-foreground">Employee Salary Details</p>
      {details.monthlySalaryEntries.length > 0 && (
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="px-3 py-2 bg-muted/50">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Monthly Salary</p>
          </div>
          <div className="divide-y divide-border">
            {details.monthlySalaryEntries.map(([label, amount]) => (
              <div key={label} className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-mono font-medium">{formatCurrency(toAmount(amount))}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-center">
        <div className="p-2.5 rounded-lg bg-blue-50">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Set Monthly Deduction</p>
          <p className="text-sm font-bold font-mono text-[#0047AB]">
            {formatCurrency(details.setMonthlyDeductionAmount)}
          </p>
        </div>
        <div className="p-2.5 rounded-lg bg-red-50">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Balance Advance</p>
          <p className="text-sm font-bold font-mono text-red-600">
            {formatCurrency(details.balanceAdvance)}
          </p>
        </div>
      </div>
      {details.lastMonths.length > 0 && (
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="px-3 py-2 bg-muted/50">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Actual Deduction (Last 6 Months)</p>
          </div>
          <div className="divide-y divide-border max-h-40 overflow-y-auto">
            {details.lastMonths.map((row, index) => (
              <div
                key={`${row.label}-${index}`}
                className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm"
              >
                <span className="text-muted-foreground">{row.label}</span>
                <span className="font-mono font-medium">{formatCurrency(row.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const normalizeEmployeeAdvanceSummary = (payload) => {
  const source = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
  const history = asAdvanceList(source);
  const subtotal = source.subtotal && typeof source.subtotal === 'object' ? source.subtotal : {};
  const approved = history.filter((item) => item?.status && item.status !== 'Requested');
  const ongoing = history.filter((item) => item?.status === 'Active');

  return {
    totalAmount: toAmount(
      subtotal.totalAmount,
      approved.reduce((sum, item) => sum + toAmount(item?.totalAmount), 0)
    ),
    monthlyDeduction: toAmount(
      subtotal.monthlyDeduction,
      ongoing.reduce((sum, item) => sum + toAmount(item?.monthlyDeduction), 0)
    ),
    remainingAmount: toAmount(
      subtotal.remainingAmount,
      ongoing.reduce((sum, item) => sum + toAmount(item?.remainingAmount), 0)
    ),
    recoveredAmount: toAmount(
      subtotal.recovered ?? subtotal.recoveredAmount,
      approved.reduce((sum, item) => sum + toAmount(item?.recovered), 0)
    ),
    history,
  };
};

const EmployeeAdvanceHistory = ({ loading, error, summary, currentAdvanceId, statusColor }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-5 text-sm text-muted-foreground">
        <div className="animate-spin w-5 h-5 border-2 border-[#0047AB] border-t-transparent rounded-full mr-2" />
        Loading previous advances...
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-red-600">{error}</p>;
  }

  const previous = (summary?.history || []).filter((item) => item?.advanceId !== currentAdvanceId);

  return (
    <div className="space-y-3" data-testid="employee-advance-history">
      <p className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
        <History className="w-3.5 h-3.5" /> Previous Advance History
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
        <div className="p-2.5 rounded-lg bg-muted">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Taken</p>
          <p className="text-sm font-bold font-mono">{formatCurrency(summary?.totalAmount || 0)}</p>
        </div>
        <div className="p-2.5 rounded-lg bg-blue-50">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Monthly Deduction</p>
          <p className="text-sm font-bold font-mono text-[#0047AB]">{formatCurrency(summary?.monthlyDeduction || 0)}</p>
        </div>
        <div className="p-2.5 rounded-lg bg-green-50">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Recovered</p>
          <p className="text-sm font-bold font-mono text-green-600">{formatCurrency(summary?.recoveredAmount || 0)}</p>
        </div>
        <div className="p-2.5 rounded-lg bg-red-50">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Outstanding</p>
          <p className="text-sm font-bold font-mono text-red-600">{formatCurrency(summary?.remainingAmount || 0)}</p>
        </div>
      </div>
      {previous.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">No previous advances for this employee</p>
      ) : (
        <div className="max-h-48 overflow-y-auto space-y-2">
          {previous.map((adv, index) => {
            const recovered = toAmount(adv?.recovered, Math.max(toAmount(adv?.totalAmount) - toAmount(adv?.remainingAmount), 0));
            const isOngoing = adv?.status === 'Active';
            return (
              <div
                key={adv?.advanceId || `${adv?.createdAt || 'advance'}-${index}`}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2.5 rounded-lg border border-border"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium font-mono">{adv?.advanceId || '-'}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatRequestedDate(adv?.createdAt || adv?.requestedAt || adv?.date)}
                    {adv?.startMonth ? ` · ${MONTHS[adv.startMonth - 1]} ${adv.startYear || ''}` : ''}
                  </p>
                </div>
                <div className="flex items-center justify-between sm:justify-end gap-3">
                  <div className="text-right">
                    <p className="text-sm font-mono font-medium">{formatCurrency(adv?.totalAmount || 0)}</p>
                    {isOngoing && (
                      <p className="text-xs font-mono text-[#0047AB]">
                        {formatCurrency(adv?.monthlyDeduction || 0)}/mo
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground font-mono">
                      Rec. {formatCurrency(recovered)} · Bal. {formatCurrency(adv?.remainingAmount || 0)}
                    </p>
                  </div>
                  <Badge className={statusColor(adv?.status)}>{adv?.status || '-'}</Badge>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function AdvancesPage() {
  const { user } = useAuth();
  const canEdit = canEditAdvances(user?.role);
  const canApprove = canApproveAdvances(user?.role);
  const [advances, setAdvances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('requests');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [slipOpen, setSlipOpen] = useState(false);
  const [slipAdvance, setSlipAdvance] = useState(null);
  const [slipEmployee, setSlipEmployee] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [selectedAdvance, setSelectedAdvance] = useState(null);
  const [employeeSummary, setEmployeeSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [advanceDetails, setAdvanceDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [approveForm, setApproveForm] = useState({
    startMonth: String(new Date().getMonth() + 1),
    startYear: String(new Date().getFullYear()),
  });
  const [editingAdvanceId, setEditingAdvanceId] = useState(null);
  const [savingAdvance, setSavingAdvance] = useState(false);
  const [form, setForm] = useState(emptyAdvanceForm);

  const [totalAdvances, setTotalAdvances] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [requestedCount, setRequestedCount] = useState(0);

  const normalizeArrayResponse = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    if (Array.isArray(value?.items)) return value.items;
    return [];
  };

  const normalizeListResponse = (value) => {
    if (Array.isArray(value)) return { items: value, total: value.length, page: 1, pages: 1 };
    if (Array.isArray(value?.items)) return { items: value.items, total: value?.total ?? value.items.length, page: value?.page ?? 1, pages: value?.pages ?? 1 };
    return { items: [], total: 0, page: 1, pages: 1 };
  };

  const queryFilters = useMemo(() => {
    const params = { page, limit: pageSize };
    const trimmedSearch = debouncedSearch?.trim?.();
    if (trimmedSearch) params.search = trimmedSearch;
    if (activeTab === 'requests') params.status = 'Requested';
    return params;
  }, [page, pageSize, debouncedSearch, activeTab]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeTab]);

  useEffect(() => {
    let cancelled = false;
    const fetchEmployees = async () => {
      try {
        const empsData = await employeeApi.getAll().catch(() => []);
        if (cancelled) return;
        const normalizedEmployees = normalizeArrayResponse(empsData);
        setEmployees(normalizedEmployees.filter((e) => e?.status === 'Active'));
      } catch (err) {
        console.error('Failed to fetch employees:', err);
      }
    };
    fetchEmployees();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchAdvances = async () => {
      if (!loading) setTableLoading(true);
      try {
        const advsData = await advanceApi.getAll(queryFilters);
        if (cancelled) return;
        const normalizedAdvances = normalizeListResponse(advsData);
        setAdvances(normalizedAdvances.items);
        setTotalAdvances(normalizedAdvances.total);
        setTotalPages(normalizedAdvances.pages);
        setPage((p) => clampPageToTotal(p, normalizedAdvances.pages));
        if (activeTab === 'requests') {
          setRequestedCount(normalizedAdvances.total);
        }
      } catch (err) {
        console.error('Failed to fetch advances:', err);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setTableLoading(false);
        }
      }
    };
    fetchAdvances();
    return () => {
      cancelled = true;
    };
  }, [queryFilters, activeTab]);

  useEffect(() => {
    let cancelled = false;
    const fetchRequestedCount = async () => {
      if (activeTab === 'requests') return;
      try {
        const data = await advanceApi.getAll({ status: 'Requested', page: 1, limit: 1 }).catch(() => null);
        if (cancelled || !data) return;
        const normalized = normalizeListResponse(data);
        setRequestedCount(normalized.total);
      } catch {
        /* ignore badge count errors */
      }
    };
    fetchRequestedCount();
    return () => {
      cancelled = true;
    };
  }, [activeTab, advances]);

  useEffect(() => {
    const advanceId = selectedAdvance?.advanceId;
    const employeeId = selectedAdvance?.employeeId;
    if ((!approveOpen && !rejectOpen) || !advanceId) return undefined;

    let cancelled = false;
    const fetchReviewContext = async () => {
      setDetailsLoading(true);
      setSummaryLoading(true);
      setDetailsError('');
      setSummaryError('');
      try {
        const detailsPayload = await advanceApi.getDetails(advanceId);
        if (cancelled) return;
        const details = normalizeAdvanceDetails(detailsPayload);
        setAdvanceDetails(details);

        const fromDetails = normalizeEmployeeAdvanceSummary(details.oldAdvanceSource);
        const hasOldAdvance =
          fromDetails.history.length > 0 ||
          fromDetails.totalAmount > 0 ||
          fromDetails.remainingAmount > 0 ||
          fromDetails.monthlyDeduction > 0;

        if (hasOldAdvance) {
          setEmployeeSummary(fromDetails);
          setSummaryLoading(false);
        } else if (employeeId) {
          try {
            const data = await advanceApi.getEmployeeSummary(employeeId);
            if (cancelled) return;
            setEmployeeSummary(normalizeEmployeeAdvanceSummary(data));
          } catch (err) {
            if (cancelled) return;
            setEmployeeSummary(null);
            setSummaryError(err?.message || 'Failed to load previous advances');
          } finally {
            if (!cancelled) setSummaryLoading(false);
          }
        } else {
          setEmployeeSummary(fromDetails);
          setSummaryLoading(false);
        }
      } catch (err) {
        if (cancelled) return;
        setAdvanceDetails(null);
        setDetailsError(err?.message || 'Failed to load salary details');
        setSummaryLoading(false);
        if (employeeId) {
          try {
            const data = await advanceApi.getEmployeeSummary(employeeId);
            if (cancelled) return;
            setEmployeeSummary(normalizeEmployeeAdvanceSummary(data));
            setSummaryError('');
          } catch (summaryErr) {
            if (cancelled) return;
            setEmployeeSummary(null);
            setSummaryError(summaryErr?.message || 'Failed to load previous advances');
          }
        }
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    };

    fetchReviewContext();
    return () => {
      cancelled = true;
    };
  }, [approveOpen, rejectOpen, selectedAdvance?.advanceId, selectedAdvance?.employeeId]);

  const handleCreate = async () => {
    if (!form.employeeId || !form.totalAmount || !form.monthlyDeduction) {
      toast.error('Please fill all required fields');
      return;
    }
    if (parseInt(form.monthlyDeduction, 10) > parseInt(form.totalAmount, 10)) {
      toast.error('Monthly deduction cannot exceed total amount');
      return;
    }
    setSavingAdvance(true);
    try {
      const payload = {
        employeeId: form.employeeId,
        totalAmount: parseInt(form.totalAmount, 10),
        monthlyDeduction: parseInt(form.monthlyDeduction, 10),
        startMonth: parseInt(form.startMonth, 10),
        startYear: parseInt(form.startYear, 10),
        reason: form.reason,
      };

      if (editingAdvanceId) {
        const updated = await advanceApi.update(editingAdvanceId, payload);
        setAdvances((prev) =>
          prev.map((item) =>
            item.advanceId === editingAdvanceId ? { ...item, ...updated } : item
          )
        );
        toast.success('Advance updated');
      } else {
        const newAdvance = await advanceApi.create(payload);
        if (activeTab === 'requests' && (newAdvance?.status === 'Requested' || !newAdvance?.status)) {
          setAdvances((prev) => [newAdvance, ...prev]);
          setTotalAdvances((prev) => prev + 1);
          setRequestedCount((prev) => prev + 1);
        } else if (activeTab === 'records' && newAdvance?.status !== 'Requested') {
          setAdvances((prev) => [newAdvance, ...prev]);
          setTotalAdvances((prev) => prev + 1);
        } else if (newAdvance?.status === 'Requested') {
          setRequestedCount((prev) => prev + 1);
        }
        toast.success('Advance created successfully');
      }

      setAdvanceOpen(false);
      setEditingAdvanceId(null);
      setForm(emptyAdvanceForm());
    } catch (err) {
      toast.error(err.message || (editingAdvanceId ? 'Failed to update advance' : 'Failed to create advance'));
    } finally {
      setSavingAdvance(false);
    }
  };

  const handleOpenCreate = () => {
    if (!canEdit) return;
    setEditingAdvanceId(null);
    setForm(emptyAdvanceForm());
    setAdvanceOpen(true);
  };

  const canEditAdvanceRecord = (advance) =>
    canEdit && !!advance?.advanceId && advance?.status !== 'Cancelled';

  const handleOpenEdit = (advance) => {
    if (!canEditAdvanceRecord(advance)) return;
    setEditingAdvanceId(advance.advanceId);
    setForm({
      employeeId: advance.employeeId || '',
      totalAmount: String(advance.totalAmount ?? ''),
      monthlyDeduction: String(advance.monthlyDeduction ?? ''),
      startMonth: String(advance.startMonth || new Date().getMonth() + 1),
      startYear: String(advance.startYear || new Date().getFullYear()),
      reason: advance.reason || '',
    });
    setAdvanceOpen(true);
  };

  const handleCancel = async (advanceId) => {
    try {
      const updated = await advanceApi.cancel(advanceId);
      setAdvances((prev) => prev.map((a) => (a.advanceId === advanceId ? { ...a, ...updated } : a)));
      toast.success('Advance cancelled');
    } catch (err) {
      toast.error(err.message || 'Failed to cancel advance');
    }
  };

  const handleOpenCancelConfirm = (advance) => {
    if (!advance?.advanceId) return;
    setSelectedAdvance(advance);
    setConfirmCancelOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!selectedAdvance?.advanceId) return;
    await handleCancel(selectedAdvance.advanceId);
    setConfirmCancelOpen(false);
    setSelectedAdvance(null);
  };

  const handleOpenApprove = (advance) => {
    if (!canApprove || !advance?.advanceId) return;
    setSelectedAdvance(advance);
    setEmployeeSummary(null);
    setSummaryError('');
    setAdvanceDetails(null);
    setDetailsError('');
    setApproveForm({
      startMonth: String(advance?.startMonth || new Date().getMonth() + 1),
      startYear: String(advance?.startYear || new Date().getFullYear()),
    });
    setApproveOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!canApprove || !selectedAdvance?.advanceId) return;
    setStatusUpdating(true);
    try {
      await advanceApi.updateStatus(selectedAdvance.advanceId, {
        status: 'Active',
        startMonth: parseInt(approveForm.startMonth, 10),
        startYear: parseInt(approveForm.startYear, 10),
      });
      setAdvances((prev) => prev.filter((a) => a.advanceId !== selectedAdvance.advanceId));
      setTotalAdvances((prev) => Math.max(prev - 1, 0));
      setRequestedCount((prev) => Math.max(prev - 1, 0));
      toast.success('Advance approved');
      setApproveOpen(false);
      setSelectedAdvance(null);
    } catch (err) {
      toast.error(err.message || 'Failed to approve advance');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleOpenReject = (advance) => {
    if (!canApprove || !advance?.advanceId) return;
    setSelectedAdvance(advance);
    setEmployeeSummary(null);
    setSummaryError('');
    setAdvanceDetails(null);
    setDetailsError('');
    setRejectOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!canApprove || !selectedAdvance?.advanceId) return;
    setStatusUpdating(true);
    try {
      await advanceApi.updateStatus(selectedAdvance.advanceId, {
        status: 'Cancelled',
      });
      setAdvances((prev) => prev.filter((a) => a.advanceId !== selectedAdvance.advanceId));
      setTotalAdvances((prev) => Math.max(prev - 1, 0));
      setRequestedCount((prev) => Math.max(prev - 1, 0));
      toast.success('Advance request rejected');
      setRejectOpen(false);
      setSelectedAdvance(null);
    } catch (err) {
      toast.error(err.message || 'Failed to reject advance');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleExportAdvances = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const response = await advanceApi.export();
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const matchedFileName = disposition.match(/filename="?([^"]+)"?/i)?.[1];
      const fileName = matchedFileName || `advances_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      downloadBlobFile(blob, fileName);
      toast.success('Advance records exported');
    } catch (err) {
      toast.error(err?.message || 'Failed to export advance records');
    } finally {
      setExporting(false);
    }
  };

  const handleOpenSlip = (adv) => {
    if (!adv?.advanceId) return;
    const fullEmp = employees.find(
      (e) => e?.empId === adv.employeeId || e?.id === adv.employeeId
    );
    setSlipEmployee(fullEmp || null);
    setSlipAdvance(adv);
    setSlipOpen(true);
  };

  const statusColor = (status) => {
    if (status === 'Active') return 'bg-blue-100 text-blue-700 border-blue-200';
    if (status === 'Completed') return 'bg-green-100 text-green-700 border-green-200';
    if (status === 'Requested') return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-red-100 text-red-700 border-red-200';
  };

  const totalActive = advances.filter((a) => a.status === 'Active').reduce((s, a) => s + (a.remainingAmount || 0), 0);
  const totalDeducted = advances.reduce((s, a) => {
    const deducted = (a.deductionHistory || []).reduce((ds, d) => ds + (d.amount || 0), 0);
    return s + deducted;
  }, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="advances-page" className="space-y-3 animate-fadeIn">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-[Manrope]">Advance Payments</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            {canEdit ? 'Manage employee salary advances' : 'View employee salary advances'}
          </p>
        </div>
        {canEdit && (
          <Button onClick={handleOpenCreate} className="bg-[#0047AB] hover:bg-[#003d96] text-white" data-testid="add-advance-btn">
            <Plus className="w-4 h-4 mr-2" /> New Advance
          </Button>
        )}
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          setActiveTab(value);
          setPage(1);
          setSearch('');
          setDebouncedSearch('');
        }}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="requests" data-testid="tab-advance-requests">
            Advance Requests
            {requestedCount > 0 && (
              <Badge className="ml-2 bg-yellow-100 text-yellow-700 border-yellow-200 text-xs">{requestedCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="records" data-testid="tab-advance-records">
            Advance Records
          </TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="space-y-4">
          <Card className="border border-border shadow-sm">
            <CardHeader className="py-3">
              <div className="relative w-full sm:max-w-sm">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  data-testid="advance-requests-search"
                  placeholder="Search by name, ID, or advance ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-xs uppercase tracking-widest">ID</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Employee</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Requested Date</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Total Amount</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Monthly Deduction</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Reason</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                    {(canApprove || canEdit) && <TableHead className="text-xs uppercase tracking-widest">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableLoading && (
                    <TableRow>
                      <TableCell colSpan={canApprove || canEdit ? 8 : 7} className="text-center py-6 text-muted-foreground">
                        Loading requests...
                      </TableCell>
                    </TableRow>
                  )}
                  {!tableLoading && advances.map((adv) => (
                    <TableRow key={adv.advanceId} data-testid={`advance-request-row-${adv.advanceId}`}>
                      <TableCell className="font-mono text-xs text-muted-foreground">{adv.advanceId}</TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium">{adv.employeeName}</p>
                          <p className="text-xs text-muted-foreground font-mono">{adv.employeeId}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm font-mono">{formatRequestedDate(adv.createdAt)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(adv.totalAmount)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(adv.monthlyDeduction)}</TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate">{adv.reason || '-'}</TableCell>
                      <TableCell><Badge className={statusColor(adv.status)}>{adv.status}</Badge></TableCell>
                      {(canApprove || canEdit) && (
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {canEditAdvanceRecord(adv) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7"
                                onClick={() => handleOpenEdit(adv)}
                                data-testid={`edit-advance-${adv.advanceId}`}
                              >
                                <Pencil className="w-3 h-3 mr-1" /> Edit
                              </Button>
                            )}
                            {canApprove && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-green-600 border-green-200 hover:bg-green-50"
                                  onClick={() => handleOpenApprove(adv)}
                                  data-testid={`approve-advance-${adv.advanceId}`}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" /> Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => handleOpenReject(adv)}
                                  data-testid={`reject-advance-${adv.advanceId}`}
                                >
                                  <XCircle className="w-3 h-3 mr-1" /> Reject
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {!tableLoading && totalAdvances === 0 && (
                    <TableRow>
                      <TableCell colSpan={canApprove || canEdit ? 8 : 7} className="text-center py-12 text-muted-foreground">
                        <Wallet className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>{debouncedSearch?.trim() ? 'No advance requests match your search' : 'No pending advance requests'}</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages} ({totalAdvances} requests)
              </p>
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-muted-foreground">Show</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => {
                    setPageSize(Number(v));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[70px] h-8 text-sm" data-testid="advance-requests-page-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">per page</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page <= 1}
                data-testid="advance-requests-prev-page-btn"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={page >= totalPages}
                data-testid="advance-requests-next-page-btn"
              >
                Next
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="records" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <Card className="border border-border shadow-sm">
              <CardContent className="p-3 sm:p-4 text-center">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Advances</p>
                <p className="text-xl sm:text-2xl font-bold font-mono mt-1 text-[#0047AB]">{totalAdvances}</p>
              </CardContent>
            </Card>
            <Card className="border border-border shadow-sm">
              <CardContent className="p-3 sm:p-4 text-center">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Active</p>
                <p className="text-xl sm:text-2xl font-bold font-mono mt-1 text-[#f59e0b]">{advances.filter((a) => a.status === 'Active').length}</p>
              </CardContent>
            </Card>
            <Card className="border border-border shadow-sm">
              <CardContent className="p-3 sm:p-4 text-center">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Outstanding</p>
                <p className="text-xl sm:text-2xl font-bold font-mono mt-1 text-[#ef4444]">{formatCurrency(totalActive)}</p>
              </CardContent>
            </Card>
            <Card className="border border-border shadow-sm">
              <CardContent className="p-3 sm:p-4 text-center">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Recovered</p>
                <p className="text-xl sm:text-2xl font-bold font-mono mt-1 text-[#10b981]">{formatCurrency(totalDeducted)}</p>
              </CardContent>
            </Card>
          </div>

          <Card className="border border-border shadow-sm">
            <CardHeader className="py-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-base font-[Manrope] flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-[#0047AB]" />
                  Advance Records
                </CardTitle>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:ml-auto w-full sm:w-auto">
                  <div className="relative w-full sm:max-w-sm">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="advance-search"
                      placeholder="Search by name, ID, or advance ID..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleExportAdvances}
                    disabled={exporting || tableLoading}
                    data-testid="advances-export-btn"
                    className="shrink-0"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {exporting ? 'Exporting...' : 'Export'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-xs uppercase tracking-widest">ID</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Employee</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Requested Date</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Total Amount</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Monthly Deduction</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Remaining</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Start</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Progress</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableLoading && (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-6 text-muted-foreground">
                        Loading advances...
                      </TableCell>
                    </TableRow>
                  )}
                  {!tableLoading && advances.map((adv) => {
                    const progress = adv.totalAmount
                      ? ((adv.totalAmount - (adv.remainingAmount || 0)) / adv.totalAmount) * 100
                      : 0;
                    return (
                      <TableRow key={adv.advanceId} data-testid={`advance-row-${adv.advanceId}`}>
                        <TableCell className="font-mono text-xs text-muted-foreground">{adv.advanceId}</TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm font-medium">{adv.employeeName}</p>
                            <p className="text-xs text-muted-foreground font-mono">{adv.employeeId}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm font-mono">{formatRequestedDate(adv.createdAt)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatCurrency(adv.totalAmount)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatCurrency(adv.monthlyDeduction)}</TableCell>
                        <TableCell className="text-right font-mono text-sm font-bold text-[#ef4444]">{formatCurrency(adv.remainingAmount)}</TableCell>
                        <TableCell className="text-sm">
                          {adv?.startMonth ? `${MONTHS[adv.startMonth - 1]} ${adv.startYear}` : '-'}
                        </TableCell>
                        <TableCell><Badge className={statusColor(adv.status)}>{adv.status}</Badge></TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-[#0047AB] transition-all" style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }} />
                            </div>
                            <span className="text-xs font-mono text-muted-foreground">{Math.round(progress)}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {canEditAdvanceRecord(adv) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7"
                                onClick={() => handleOpenEdit(adv)}
                                data-testid={`edit-advance-${adv.advanceId}`}
                              >
                                <Pencil className="w-3 h-3 mr-1" /> Edit
                              </Button>
                            )}
                            {(adv.status === 'Active' || adv.status === 'Completed') && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7"
                                onClick={() => handleOpenSlip(adv)}
                                data-testid={`advance-slip-btn-${adv.advanceId}`}
                              >
                                <Printer className="w-3 h-3 mr-1" /> Slip
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7"
                              onClick={() => { setSelectedAdvance(adv); setHistoryOpen(true); }}
                              data-testid={`history-advance-${adv.advanceId}`}
                            >
                              <History className="w-3 h-3 mr-1" /> History
                            </Button>
                            {canEdit && adv.status === 'Active' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-red-500 hover:text-red-700"
                                onClick={() => handleOpenCancelConfirm(adv)}
                                data-testid={`cancel-advance-${adv.advanceId}`}
                              >
                                <Ban className="w-3 h-3 mr-1" /> Cancel
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!tableLoading && totalAdvances === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-12 text-muted-foreground">
                        <Wallet className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>{debouncedSearch?.trim() ? 'No advances match your search' : 'No advance records'}</p>
                        {canEdit && !debouncedSearch?.trim() && (
                          <p className="text-xs mt-1">Click &quot;New Advance&quot; to create one</p>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages} ({totalAdvances} records)
              </p>
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-muted-foreground">Show</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => {
                    setPageSize(Number(v));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[70px] h-8 text-sm" data-testid="advances-page-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">per page</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page <= 1}
                data-testid="advances-prev-page-btn"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={page >= totalPages}
                data-testid="advances-next-page-btn"
              >
                Next
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {canEdit && (
        <Dialog
          open={advanceOpen}
          onOpenChange={(open) => {
            setAdvanceOpen(open);
            if (!open) {
              setEditingAdvanceId(null);
              setForm(emptyAdvanceForm());
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-[Manrope]">
                {editingAdvanceId ? 'Edit Advance Payment' : 'New Advance Payment'}
              </DialogTitle>
              <DialogDescription>
                {editingAdvanceId
                  ? 'Update amount, monthly deduction, start period, or reason.'
                  : 'Create a salary advance for an employee'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Employee *</Label>
                <Select
                  value={form.employeeId}
                  onValueChange={(v) => setForm((p) => ({ ...p, employeeId: v }))}
                  disabled={!!editingAdvanceId}
                >
                  <SelectTrigger data-testid="advance-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.empId || emp.id} value={emp.empId || emp.id}>
                        {emp.firstName} {emp.lastName} ({emp.empId || emp.id})
                      </SelectItem>
                    ))}
                    {editingAdvanceId &&
                      form.employeeId &&
                      !employees.some((emp) => (emp.empId || emp.id) === form.employeeId) && (
                        <SelectItem value={form.employeeId}>{form.employeeId}</SelectItem>
                      )}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Total Amount *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-sm text-muted-foreground font-mono">&#8377;</span>
                    <Input
                      type="number"
                      value={form.totalAmount}
                      onChange={(e) => setForm((p) => ({ ...p, totalAmount: e.target.value }))}
                      className="pl-7 font-mono"
                      placeholder="10000"
                      data-testid="advance-total-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Monthly Deduction *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-sm text-muted-foreground font-mono">&#8377;</span>
                    <Input
                      type="number"
                      value={form.monthlyDeduction}
                      onChange={(e) => setForm((p) => ({ ...p, monthlyDeduction: e.target.value }))}
                      className="pl-7 font-mono"
                      placeholder="2000"
                      data-testid="advance-monthly-input"
                    />
                  </div>
                </div>
              </div>
              {form.totalAmount && form.monthlyDeduction && parseInt(form.monthlyDeduction, 10) > 0 && (
                <div className="p-3 rounded-lg bg-muted text-center">
                  <p className="text-xs text-muted-foreground">Recovery Period</p>
                  <p className="text-lg font-bold font-mono text-[#0047AB]">
                    {Math.ceil(parseInt(form.totalAmount, 10) / parseInt(form.monthlyDeduction, 10))} months
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Start Month</Label>
                  <Select value={form.startMonth} onValueChange={(v) => setForm((p) => ({ ...p, startMonth: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Start Year</Label>
                  <Select value={form.startYear} onValueChange={(v) => setForm((p) => ({ ...p, startYear: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2025">2025</SelectItem>
                      <SelectItem value="2026">2026</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reason</Label>
                <Textarea
                  value={form.reason}
                  onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
                  placeholder="Reason for advance..."
                  data-testid="advance-reason-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setAdvanceOpen(false)}
                disabled={savingAdvance}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={savingAdvance}
                className="bg-[#0047AB] hover:bg-[#003d96] text-white"
                data-testid="save-advance-btn"
              >
                {savingAdvance
                  ? editingAdvanceId
                    ? 'Saving...'
                    : 'Creating...'
                  : editingAdvanceId
                    ? 'Save Changes'
                    : 'Create Advance'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {canApprove && (
        <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
          <DialogContent className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-[Manrope]">Approve Advance</DialogTitle>
              <DialogDescription>
                Set the deduction start month and activate this advance request.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm space-y-1">
              <p><span className="text-muted-foreground">Employee:</span> <strong>{selectedAdvance?.employeeName || '-'}</strong></p>
              <p><span className="text-muted-foreground">Joining Date:</span> <strong>{formatRequestedDate(advanceDetails?.joiningDate)}</strong></p>
              <p><span className="text-muted-foreground">Advance ID:</span> <span className="font-mono">{selectedAdvance?.advanceId || '-'}</span></p>
              <p><span className="text-muted-foreground">Amount:</span> <span className="font-mono">{formatCurrency(selectedAdvance?.totalAmount || 0)}</span></p>
            </div>
            <AdvanceReviewSalaryDetails
              loading={detailsLoading}
              error={detailsError}
              details={advanceDetails}
            />
            <EmployeeAdvanceHistory
              loading={summaryLoading}
              error={summaryError}
              summary={employeeSummary}
              currentAdvanceId={selectedAdvance?.advanceId}
              statusColor={statusColor}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Start Month *</Label>
                <Select value={approveForm.startMonth} onValueChange={(v) => setApproveForm((p) => ({ ...p, startMonth: v }))}>
                  <SelectTrigger data-testid="approve-start-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Start Year *</Label>
                <Select value={approveForm.startYear} onValueChange={(v) => setApproveForm((p) => ({ ...p, startYear: v }))}>
                  <SelectTrigger data-testid="approve-start-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2025">2025</SelectItem>
                    <SelectItem value="2026">2026</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setApproveOpen(false)} disabled={statusUpdating}>Cancel</Button>
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={handleConfirmApprove}
                disabled={statusUpdating}
                data-testid="confirm-approve-advance-btn"
              >
                {statusUpdating ? 'Approving...' : 'Approve'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {canApprove && (
        <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
          <DialogContent className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-[Manrope]">Reject Advance Request?</DialogTitle>
              <DialogDescription>
                This will cancel the advance request.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm space-y-1">
              <p><span className="text-muted-foreground">Employee:</span> <strong>{selectedAdvance?.employeeName || '-'}</strong></p>
              <p><span className="text-muted-foreground">Joining Date:</span> <strong>{formatRequestedDate(advanceDetails?.joiningDate)}</strong></p>
              <p><span className="text-muted-foreground">Advance ID:</span> <span className="font-mono">{selectedAdvance?.advanceId || '-'}</span></p>
              <p><span className="text-muted-foreground">Amount:</span> <span className="font-mono">{formatCurrency(selectedAdvance?.totalAmount || 0)}</span></p>
            </div>
            <AdvanceReviewSalaryDetails
              loading={detailsLoading}
              error={detailsError}
              details={advanceDetails}
            />
            <EmployeeAdvanceHistory
              loading={summaryLoading}
              error={summaryError}
              summary={employeeSummary}
              currentAdvanceId={selectedAdvance?.advanceId}
              statusColor={statusColor}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectOpen(false)} disabled={statusUpdating}>Keep Request</Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleConfirmReject}
                disabled={statusUpdating}
                data-testid="confirm-reject-advance-btn"
              >
                {statusUpdating ? 'Rejecting...' : 'Reject'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <AdvanceSlipModal
        open={slipOpen}
        onOpenChange={(open) => {
          setSlipOpen(open);
          if (!open) {
            setSlipAdvance(null);
            setSlipEmployee(null);
          }
        }}
        employee={slipEmployee}
        advance={slipAdvance}
      />

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Deduction History</DialogTitle>
            <DialogDescription>
              {selectedAdvance?.employeeName} - {formatCurrency(selectedAdvance?.totalAmount || 0)} advance
            </DialogDescription>
          </DialogHeader>
          {selectedAdvance && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-xs text-muted-foreground">Total</p>
                  <p className="text-sm font-bold font-mono">{formatCurrency(selectedAdvance.totalAmount)}</p>
                </div>
                <div className="p-3 rounded-lg bg-green-50">
                  <p className="text-xs text-muted-foreground">Recovered</p>
                  <p className="text-sm font-bold font-mono text-green-600">{formatCurrency(selectedAdvance.totalAmount - selectedAdvance.remainingAmount)}</p>
                </div>
                <div className="p-3 rounded-lg bg-red-50">
                  <p className="text-xs text-muted-foreground">Remaining</p>
                  <p className="text-sm font-bold font-mono text-red-600">{formatCurrency(selectedAdvance.remainingAmount)}</p>
                </div>
              </div>
              {(selectedAdvance.deductionHistory || []).length > 0 ? (
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {selectedAdvance.deductionHistory.map((d, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-border">
                      <div className="flex items-center gap-2">
                        <TrendingDown className="w-4 h-4 text-green-600" />
                        <div>
                          <p className="text-sm font-medium">{MONTHS[d.month - 1]} {d.year}</p>
                          <p className="text-xs text-muted-foreground font-mono">{d.payRunId}</p>
                        </div>
                      </div>
                      <span className="text-sm font-bold font-mono text-green-600">-{formatCurrency(d.amount)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No deductions yet</p>
                  <p className="text-xs mt-1">Deductions will appear here after payroll processing</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {canEdit && (
        <Dialog open={confirmCancelOpen} onOpenChange={setConfirmCancelOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-[Manrope]">Cancel Advance?</DialogTitle>
              <DialogDescription>
                This will stop future deductions for this advance.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
              <p><span className="text-muted-foreground">Employee:</span> <strong>{selectedAdvance?.employeeName || '-'}</strong></p>
              <p><span className="text-muted-foreground">Advance ID:</span> <span className="font-mono">{selectedAdvance?.advanceId || '-'}</span></p>
              <p><span className="text-muted-foreground">Remaining:</span> <span className="font-mono">{formatCurrency(selectedAdvance?.remainingAmount || 0)}</span></p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmCancelOpen(false)}>Keep Active</Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleConfirmCancel}
                data-testid="confirm-cancel-advance-btn"
              >
                Confirm Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
